/**
 * MatureLearnerLanding — `/pathways/mature-learner` explainer.
 *
 * Serves the 25+ career-changer cohort: parents returning to study,
 * mid-career switchers, professionals topping up an HND, international
 * mid-career applicants. Same shape as the foundation-year landing
 * but framed around Access to HE Diplomas, HND-to-degree top-ups
 * and employer sponsorship.
 *
 * Cross-links deeply into `/uk-fe-colleges` (where the top-up degrees
 * actually run) and `/pathways/foundation-year` (the sibling route
 * for younger near-miss applicants).
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  ShieldCheck,
  Sparkles,
  Clock,
  Building2,
  Briefcase,
  Wallet,
} from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";

export default function MatureLearnerLanding() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="mature-learner-landing">
      <PathwaySEO
        title="Coming back to study at 25+ — four routes into a UK bachelor's"
        description="Career-changer, parent returner, mid-career switcher? Four established pathways into a full UK degree past 25: Access to HE Diploma, HND-to-degree top-up, part-time bachelor's, and employer-sponsored apprenticeships."
        path="/pathways/mature-learner"
      />
      {/* Hero */}
      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Pathway explainer
        </div>
        <h1
          data-testid="mature-learner-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          Coming back to study at 25+
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed text-lg">
          You're 27, 34, 45. You want a proper degree — not another
          weekend micro-credential — but you're not doing UCAS at 17
          again and you're not moving into halls with 18-year-olds.
        </p>
        <p className="mt-3 text-stone-600 max-w-3xl leading-relaxed">
          The mature-learner route is a mature product. There are four
          established pathways into a full UK bachelor's, all costed
          under the standard student-finance system, most compatible
          with part-time study and employer sponsorship. This page
          walks through each.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-6 rounded-lg border border-[#4C1D95]/30 bg-[#4C1D95]/[0.03] p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-editorial text-xl text-[#002147]">
            See providers set up for mature learners
          </div>
          <div className="text-[13px] text-stone-600 mt-1">
            The UK's 281 FE colleges and specialist HE providers we index
            — where top-ups, Access to HE and HNDs actually run.
          </div>
        </div>
        <Link
          to="/uk-fe-colleges"
          data-testid="mature-learner-cta"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-[#3a1476]"
        >
          Browse mature-friendly providers <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Four routes */}
      <div className="mt-10">
        <div className="label-uppercase text-[#4C1D95] font-semibold">
          Four established routes
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Route
            icon={<Compass className="h-4 w-4" />}
            title="Access to HE Diploma"
            body={
              "A 1-year QAA-regulated qualification designed specifically " +
              "for adults returning to education. Delivered at FE colleges " +
              "in evenings, weekends or full-time. On completion, you're " +
              "treated as UCAS Tariff-equivalent to A-levels — most " +
              "universities including Russell Group members accept it. " +
              "Fee is fully written off if you go on to complete a full " +
              "degree afterwards (Advanced Learner Loan Bursary Fund)."
            }
          />
          <Route
            icon={<Building2 className="h-4 w-4" />}
            title="HND-to-degree top-up"
            body={
              "If you already hold a Higher National Diploma (Level 5, " +
              "2-year vocational qual) you can add a 1-year 'top-up' at " +
              "a university and walk out with a full BSc or BEng. Fee " +
              "for the top-up year only — cheaper than a 3-year direct- " +
              "entry route. Widely available across post-92 universities " +
              "and university centres."
            }
          />
          <Route
            icon={<Clock className="h-4 w-4" />}
            title="Part-time bachelor's"
            body={
              "Same degree, 4-6 years part-time instead of 3 full-time. " +
              "Compatible with a day job. Open University runs the largest " +
              "distance-learning bachelor's programme in the UK; Birkbeck " +
              "(University of London) runs the largest evening-only " +
              "programme. Fee is charged pro-rata per module."
            }
          />
          <Route
            icon={<Briefcase className="h-4 w-4" />}
            title="Employer-sponsored / degree apprenticeship"
            body={
              "If your employer values the qualification you're chasing, " +
              "you may qualify for a Level 6 degree apprenticeship. Your " +
              "employer pays the tuition (via the Apprenticeship Levy in " +
              "the UK), the university awards the degree, and you keep " +
              "your salary throughout. Popular for accounting, engineering, " +
              "cyber, nursing and management."
            }
          />
        </div>
      </div>

      {/* Practical notes */}
      <div className="mt-10 rounded-lg border border-amber-300 bg-amber-50/40 p-5">
        <div className="label-uppercase text-amber-900 font-semibold flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3" /> Things families should sanity-check
        </div>
        <ul className="mt-3 space-y-2 text-[13px] text-stone-800 leading-snug">
          <li>
            <strong>Mature status kicks in at 21.</strong> UCAS treats
            anyone starting a degree aged 21 or over as a mature applicant.
            Requirements are typically more flexible — most unis will
            interview and value work experience alongside academic qualifications.
          </li>
          <li>
            <strong>Student finance is still available.</strong> UK
            mature learners retain full eligibility for the tuition-fee
            loan and (means-tested) maintenance loan. There's no
            age cap on the loan.
          </li>
          <li>
            <strong>Previous study can hurt eligibility.</strong> If
            you've already completed a full undergraduate degree, you're
            technically "ELQ-status" (Equivalent or Lower Qualification)
            and your loan is limited to one extra year. Check with the
            uni's admissions office directly.
          </li>
          <li>
            <strong>Access to HE ≠ Access course to a specific uni.</strong>{" "}
            The Access to HE Diploma is a portable QAA-regulated
            qualification — it's not tied to the college that delivers
            it. You can complete it in Blackpool and use it to apply
            to Oxford. That's the entire point.
          </li>
        </ul>
      </div>

      {/* Final CTA */}
      <div className="mt-10 rounded-lg border border-emerald-300 bg-emerald-50/40 p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-editorial text-xl text-[#002147] flex items-center gap-2">
            <Wallet className="h-4 w-4 text-emerald-700" />
            Ready to explore the numbers?
          </div>
          <div className="text-[13px] text-stone-600 mt-1">
            Run advanced match filtered to FE colleges and specialist
            HE providers — the corner of the sector purpose-built for
            mature returners.
          </div>
        </div>
        <Link
          to="/advanced?inst=further_education_college"
          data-testid="mature-learner-cta-bottom"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-emerald-800"
        >
          Match me to FE college degrees <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Also see */}
      <div className="mt-6 text-[13px] text-stone-500 flex items-center gap-2">
        <Compass className="h-3 w-3" />
        Also see:{" "}
        <Link to="/pathways/foundation-year" className="text-[#4C1D95] underline">
          Foundation-year route (for A-level near-misses)
        </Link>
        {" · "}
        <Link to="/uk-fe-colleges" className="text-[#4C1D95] underline">
          UK FE colleges directory
        </Link>
      </div>
    </div>
  );
}

function Route({ icon, title, body }) {
  return (
    <div
      data-testid={`mature-route-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="rounded-md border border-stone-200 bg-white p-4"
    >
      <div className="flex items-center gap-2 text-[#4C1D95]">
        {icon}
        <div className="font-editorial text-lg text-[#002147]">{title}</div>
      </div>
      <p className="mt-2 text-[13px] text-stone-700 leading-snug">{body}</p>
    </div>
  );
}
